"use client";

import { useSearchParams } from "next/navigation";
import { useEffect } from "react";

const UTM_STORAGE_KEY = "cns_utm";

const UTM_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

/**
 * Captures UTM query parameters from the URL on app entry and persists them
 * to sessionStorage so they survive redirects (e.g. to Okta and back).
 * Run once per session; first landing with UTM wins.
 */
export function UtmCapture() {
  const searchParams = useSearchParams();

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Only set if we don't already have stored UTM (first landing wins)
    const existing = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (existing) return;

    const utm: Record<string, string> = {};
    for (const key of UTM_PARAM_KEYS) {
      const value = searchParams.get(key);
      if (value) utm[key] = value;
    }
    if (Object.keys(utm).length > 0) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
    }
  }, [searchParams]);

  return null;
}

/**
 * Reads stored UTM from sessionStorage and returns camelCase keys for the API.
 * Returns null if nothing stored or not in browser.
 */
export function getStoredUtmForPayload(): {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
} | null {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, string>;
    const result: Record<string, string> = {};
    if (parsed.utm_source) result.utmSource = parsed.utm_source;
    if (parsed.utm_medium) result.utmMedium = parsed.utm_medium;
    if (parsed.utm_campaign) result.utmCampaign = parsed.utm_campaign;
    if (parsed.utm_term) result.utmTerm = parsed.utm_term;
    if (parsed.utm_content) result.utmContent = parsed.utm_content;
    return Object.keys(result).length > 0 ? (result as ReturnType<typeof getStoredUtmForPayload>) : null;
  } catch {
    return null;
  }
}
